import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Table } from "react-super-responsive-table";
import { Loader2 } from "lucide-react";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import "../styles/TableStyles.css";

export default function RemoteStocksPage() {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [hoverRow, setHoverRow] = useState(null);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });

  const fmtDate = (v) =>
    v ? new Date(v).toLocaleDateString("cs-CZ").replace(/\. /g, ".") : "";

  const fmtDateTime = (v) =>
    v
      ? new Date(v)
          .toLocaleString("cs-CZ", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          .replace(/\. /g, ".")
      : "";

  const handleSearch = async (e) => {
    const term = e.target.value;
    setSearch(term);

    const q = term.trim();
    if (q.length < 2) {
      setFilteredData([]);
      return;
    }

    if (!user || !user.pharmacyCode) return;

    setLoading(true);
    try {
      const firma = user.firma || "LEMON";
      const pc = String(user.pharmacyCode).replace(/^0+/, "");

      const res = await fetch(
        `/api/remote-stocks/${firma}/${pc}?q=${encodeURIComponent(q)}`
      );
      const json = await res.json();
      const rows = Array.isArray(json.data) ? json.data : [];

      setData(rows);
      setFilteredData(rows);
    } catch (err) {
      console.error("Chyba načítání vzdálených skladů:", err);
    } finally {
      setLoading(false);
    }
  };

  // kurzor pro tooltip
  useEffect(() => {
    const move = (e) => setCursor({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  return (
    <div className="p-4 relative">
      <h2 className="text-2xl font-semibold mb-4">Vzdálené sklady</h2>

      <input
        type="text"
        placeholder="Vyhledat podle názvu, PDK nebo SÚKL"
        value={search}
        onChange={handleSearch}
        className="border p-2 rounded mb-4 w-full max-w-md"
      />

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Načítání…
        </div>
      ) : filteredData.length === 0 ? (
        search.length < 2 ? (
          <p className="text-gray-500">Zadejte alespoň 2 znaky.</p>
        ) : (
          <p>Nenalezeny žádné záznamy.</p>
        )
      ) : (
        <div className="relative">
          <Table className="custom-table">
            <thead>
              <tr>
                <th>PDK</th>
                <th>SÚKL</th>
                <th>Název</th>
                <th className="text-right">Množství</th>
                <th>Šarže</th>
                <th>Exspirace</th>
                <th>Pobočka</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((r, i) => (
                <tr
                  key={i}
                  onMouseEnter={() => setHoverRow(r)}
                  onMouseLeave={() => setHoverRow(null)}
                  className="cursor-pointer hover:bg-blue-50 transition-colors"
                >
                  <td>{r.PDK}</td>
                  <td>{r.SUKL}</td>
                  <td>{r.Nazev}</td>
                  <td className="text-right">{r.Množství}</td>
                  <td>{r.Sarze}</td>
                  <td>{fmtDate(r.Exspirace)}</td>
                  <td>{r.pharmacyCode}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {hoverRow && (
            <div
              className="fixed bg-white border border-blue-400 shadow-lg rounded-lg p-3 text-sm z-50"
              style={{
                top:
                  cursor.y + 200 > window.innerHeight
                    ? cursor.y - 160
                    : cursor.y + 20,
                left:
                  cursor.x + 320 > window.innerWidth
                    ? cursor.x - 320
                    : cursor.x + 20,
                width: 280,
              }}
            >
              <div className="font-bold text-blue-600 mb-1">
                Pobočka {hoverRow.pharmacyCode}
              </div>
              <div className="mb-2 text-gray-700">
                {hoverRow.pharmacyName || "Neznámá pobočka"}
              </div>

              <div>
                <strong>NCSD:</strong>{" "}
                <span className="float-right">{hoverRow.NCSD ?? "-"}</span>
              </div>
              <div>
                <strong>PCSD:</strong>{" "}
                <span className="float-right">{hoverRow.PCSD ?? "-"}</span>
              </div>
              <div>
                <strong>Výdej 6M:</strong>{" "}
                <span className="float-right">{hoverRow.vydano6m ?? "-"}</span>
              </div>
              <div className="border-t border-gray-200 my-2" />

              <div className="text-gray-600">
                <strong>Aktualizace:</strong>
                <div>{fmtDateTime(hoverRow.uploadedAt) || "-"}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
